import type { ReactNode } from "react";
import {
  CalendarDays,
  CalendarPlus,
  Clock,
  MapPin,
  Navigation,
  Package,
  Percent,
  RefreshCw,
  Tag,
  Ticket,
  Video,
  X,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@baluu/ui/components/avatar";
import { Badge } from "@baluu/ui/components/badge";
import { Button } from "@baluu/ui/components/button";
import { Card, CardContent, CardHeader } from "@baluu/ui/components/card";
import { Separator } from "@baluu/ui/components/separator";
import type { ViewContext } from "../../types";
import { AccountPaymentBlock } from "./AccountPaymentBlock";
import { PolicyPanel, QrCodePanel } from "./PolicyPanel";
import { JoinLink } from "./tabs";
import { calendarLink, legacyTotals, longDate, mapsLink, sessionLabel, ticketPrice } from "./format";
import { formatMoney } from "../../money";
import { bundleDates } from "./manageState";
import type { ManageState } from "./manageState";
import type { ClassroomPage, SessionDate } from "./types";

// The booking's receipt: picture and host, the cancellation notice, where
// and when (with directions and "Add to calendar"), the tickets and what was
// paid, the policy, Join / bundle dates / Reschedule / Cancel, and the
// check-in QR code. Port of v1's BookingReceiptCard. Reschedule and Cancel
// are links to their own pages (v1 opened the same flows in modals).

export function ReceiptCard(p: {
  ctx: ViewContext;
  page: ClassroomPage;
  state: ManageState;
  occurrence: SessionDate;
  /** When the page opened: the bundle dates shown are those after it. */
  now: number;
  joinable: boolean;
  image?: string;
  hostAvatar?: string;
  detailsUrl: string;
}) {
  const { ctx, page, state } = p;
  const { booking } = page;
  // The id from the address (v1's `data.bookingId`): for a bundle date it is
  // the date's own record, which the links and the policy are about.
  const id = page.bookingId || booking._id;
  const currency = page.ticketDetail?.currency || page.course?.currency || "GBP";
  const timezone = page.course?.timezone;
  const schoolName = page.school?.name ?? "";
  const hostName = page.teacherName ?? schoolName;
  const calendar = (date: SessionDate) => calendarLink({ title: page.className, schoolName, date, detailsUrl: p.detailsUrl });

  return (
    <Card className="overflow-hidden border-border/60 py-0 shadow-sm">
      <CardHeader className="relative p-0">
        <div className="relative aspect-[16/9] overflow-hidden bg-muted">
          {/* eslint-disable-next-line @next/next/no-img-element -- a web component, not a Next page */}
          {p.image && <img src={p.image} alt="" className="h-full w-full object-cover" loading="lazy" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          {state.isCancelled ? (
            <Badge variant="destructive" className="absolute right-3 top-3 text-xs font-semibold uppercase tracking-wide shadow-lg">
              Cancelled
            </Badge>
          ) : (
            state.isOnline && (
              <Badge className="absolute right-3 top-3 bg-primary/90 text-xs font-medium text-primary-foreground shadow-lg">
                <Video className="mr-1 h-3 w-3" />
                Online event
              </Badge>
            )
          )}
          <div className="absolute bottom-3 left-4 right-4 flex items-center gap-3">
            <Avatar className="h-10 w-10 border-2 border-white/80 shadow-md">
              {p.hostAvatar && <AvatarImage src={p.hostAvatar} alt={hostName} />}
              <AvatarFallback className="bg-primary text-sm font-semibold text-primary-foreground">{hostName.charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white drop-shadow-sm">{hostName}</p>
              {schoolName && schoolName !== hostName && <p className="truncate text-xs text-white/80 drop-shadow-sm">{schoolName}</p>}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {state.isCancelled && (
          <div className="flex items-start gap-3 border-b border-destructive/20 bg-destructive/5 px-5 py-4">
            <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
            <div>
              <p className="text-sm font-semibold text-destructive">This booking has been cancelled</p>
              {booking.cancelledObject?.cancelledAt && (
                <p className="mt-0.5 text-xs text-destructive/70">{longDate(booking.cancelledObject.cancelledAt, timezone, false)}</p>
              )}
              {booking.cancelledObject?.cancelledReason && (
                <p className="mt-1 text-xs text-destructive/70">Reason: {booking.cancelledObject.cancelledReason}</p>
              )}
            </div>
          </div>
        )}

        {!state.isOnline && page.address && (
          <Row icon={<MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}>
            <p className="text-sm font-medium">{page.address.name}</p>
            <p className="text-xs text-muted-foreground">
              {[page.address.line1, page.address.postcode ?? page.address.postCode].filter(Boolean).join(" ")}
            </p>
            <OutlineLink href={mapsLink(page.address)} icon={<Navigation className="mr-1.5 h-3 w-3" />}>
              Get directions
            </OutlineLink>
          </Row>
        )}

        {!state.isBundle && (
          <Row icon={<CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}>
            <p className="text-sm font-medium">{sessionLabel(p.occurrence, { timezone, isOnline: page.session?.isOnline })}</p>
            {calendar(p.occurrence) && (
              <OutlineLink href={calendar(p.occurrence)!} icon={<CalendarPlus className="mr-1.5 h-3 w-3" />}>
                Add to calendar
              </OutlineLink>
            )}
          </Row>
        )}

        {state.isBundle &&
          bundleDates(page.sessionsForBundle, { upcomingOnly: true, now: p.now, limit: 3 }).map((s, i) => (
            <Row key={s._id ?? i} icon={<CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}>
              <p className="text-sm font-medium">{sessionLabel(s, { timezone, isOnline: s.isOnline })}</p>
              {calendar(s) && (
                <OutlineLink href={calendar(s)!} icon={<CalendarPlus className="mr-1.5 h-3 w-3" />}>
                  Add to calendar
                </OutlineLink>
              )}
              {s.isOnline && (
                <div className="mt-1.5">
                  <JoinLink session={s} compact />
                </div>
              )}
            </Row>
          ))}

        <div className="space-y-3 border-b border-border/40 px-5 py-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold uppercase tracking-tight">Booking details</span>
            <span className="font-mono text-[11px] tracking-wide text-muted-foreground">#{id}</span>
          </div>

          {(booking.tickets ?? []).length > 0 && (
            <div className="space-y-1.5">
              {(booking.tickets ?? []).map((t, i) => {
                const price = ticketPrice(t, booking.pricingBreakdown);
                return (
                  <PriceLine key={i} icon={<Ticket className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />} label={`${t.count} × ${t.name}`}>
                    {formatMoney(price.pence, currency)}
                    {price.fullPence !== undefined && <span className="text-muted-foreground">/{formatMoney(price.fullPence, currency)}</span>}
                  </PriceLine>
                );
              })}
            </div>
          )}

          {(page.ticketDetail?.discountValue ?? 0) > 0 && (
            <PriceLine icon={<Percent className="h-3.5 w-3.5 text-muted-foreground" />} label="Discount" className="text-emerald-600">
              −{formatMoney(page.ticketDetail?.discountValue, currency)}
            </PriceLine>
          )}

          {(booking.addOns ?? []).length > 0 && (
            <div className="space-y-1.5">
              {(booking.addOns ?? []).map((a, i) => (
                <PriceLine key={i} icon={<Tag className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />} label={`${a.quantity} × ${a.name}`}>
                  {formatMoney(a.price, currency)}
                </PriceLine>
              ))}
            </div>
          )}

          <Separator />

          {page.account ? (
            <AccountPaymentBlock
              api={ctx.api}
              businessSlug={ctx.businessSlug}
              primaryColor={ctx.primaryColor}
              bookingId={id}
              account={page.account}
              timezone={booking.timezone || timezone}
            />
          ) : (
            <LegacyTotals page={page} currency={currency} />
          )}

          {booking.usedBundleID && (
            <div className="flex items-center gap-2 pt-1 text-xs">
              <Package className="h-3.5 w-3.5 text-primary" />
              <span className="font-medium">Paid with bundle credits</span>·
              <a className="font-medium text-primary hover:text-primary/80" href={ctx.links.bundle(booking.usedBundleID)}>
                Manage
              </a>
            </div>
          )}

          <PolicyPanel api={ctx.api} bookingId={id} />
        </div>

        <div className="space-y-2.5 px-5 py-4">
          {state.isOnline && !state.isBundle && (
            p.joinable ? (
              <Button asChild size="lg" className="w-full font-semibold uppercase tracking-wide">
                <a href={page.onlineJoinLink} target="_blank" rel="noopener noreferrer">
                  <Video className="mr-2 h-4 w-4" />
                  Join class
                </a>
              </Button>
            ) : (
              <Button size="lg" disabled className="w-full font-semibold uppercase tracking-wide" title="The join link opens 10 minutes before the start">
                <Video className="mr-2 h-4 w-4" />
                Join class
              </Button>
            )
          )}

          {state.isBundle && (
            <Button asChild variant="outline" className="w-full">
              <a href={ctx.links.bundleDates(id)}>
                <Clock className="mr-2 h-4 w-4" />
                Select your bundle dates
              </a>
            </Button>
          )}

          {(state.canReschedule || state.canCancel) && (
            <div className="flex gap-2">
              {state.canReschedule &&
                (state.isCancelled ? (
                  <Button variant="outline" disabled className="flex-1 text-sm">
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                    Reschedule
                  </Button>
                ) : (
                  <Button asChild variant="outline" className="flex-1 text-sm">
                    <a href={ctx.links.reschedule(id)}>
                      <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                      Reschedule
                    </a>
                  </Button>
                ))}
              {state.canCancel && (
                <Button asChild variant="ghost" className="flex-1 text-sm text-muted-foreground hover:text-destructive">
                  <a href={ctx.links.cancel(id)}>
                    <X className="mr-1.5 h-3.5 w-3.5" />
                    Cancel
                  </a>
                </Button>
              )}
            </div>
          )}
        </div>

        {state.showQrCode && (
          <>
            <Separator />
            <QrCodePanel bookingId={booking._id} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function LegacyTotals({ page, currency }: { page: ClassroomPage; currency: string }) {
  const totals = legacyTotals(page.booking);
  return (
    <>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold uppercase tracking-tight">Total</span>
        <span className="text-base font-bold tabular-nums">{formatMoney(totals.totalPence, currency)}</span>
      </div>
      {totals.deposit && (
        <>
          <div className="flex items-center justify-between gap-3 text-sm font-medium text-emerald-600">
            <span>Deposit paid</span>
            <span className="tabular-nums">{formatMoney(totals.deposit.paidPence, currency)}</span>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm font-medium text-muted-foreground">
            <span>Remaining balance</span>
            <span className="tabular-nums">{formatMoney(totals.deposit.remainingPence, currency)}</span>
          </div>
          {totals.deposit.note && <p className="text-xs text-muted-foreground">{totals.deposit.note}</p>}
        </>
      )}
    </>
  );
}

function Row({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 border-b border-border/40 px-5 py-4">
      {icon}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function OutlineLink({ href, icon, children }: { href: string; icon: ReactNode; children: ReactNode }) {
  return (
    <Button asChild variant="outline" size="sm" className="mt-2.5 h-8 text-xs font-medium">
      <a href={href} target="_blank" rel="noopener noreferrer">
        {icon}
        {children}
      </a>
    </Button>
  );
}

function PriceLine({
  icon,
  label,
  className = "",
  children,
}: {
  icon: ReactNode;
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="flex min-w-0 items-center gap-2">
        {icon}
        <span className="truncate text-sm">{label}</span>
      </span>
      <span className={`shrink-0 text-sm font-medium tabular-nums ${className}`}>{children}</span>
    </div>
  );
}
